import { useEffect, useMemo, useState } from 'react';
import {
  TenantOidcSettingsView,
  type PortalIdentityTone,
  type TenantOidcConfirmationViewModel,
  type TenantOidcObservedState,
  type TenantOidcPendingAction,
} from '@workspace/silk-ui';

import { useTenantIdentityProvidersController } from '@/features/tenant-oidc/useTenantIdentityProvidersController';

const observedTone: Record<TenantOidcObservedState, PortalIdentityTone> = {
  unvalidated: 'neutral',
  validating: 'info',
  ready: 'success',
  error: 'danger',
  deleted: 'neutral',
};

const titleCase = (value: string) => value.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());

function tenantLoginUrl(label: string): string | null {
  if (!label || typeof window === 'undefined') return null;
  const marker = 'myaccount.';
  const markerIndex = window.location.hostname.indexOf(marker);
  if (markerIndex < 0) return null;
  const baseDomain = window.location.hostname.slice(markerIndex + marker.length);
  return `${window.location.protocol}//${label}.myaccount.${baseDomain}`;
}

export default function TenantIdentityProvidersPage() {
  const controller = useTenantIdentityProvidersController();
  const provider = controller.providers[0] ?? null;
  const [confirmationKind, setConfirmationKind] = useState<'disable' | 'delete' | null>(null);

  useEffect(() => {
    if (!controller.initialized || controller.loading || controller.accessDenied || controller.editor.open) return;
    if (provider) provider.onEdit();
    else if (controller.canManage) controller.onCreate();
  }, [controller.accessDenied, controller.canManage, controller.editor.open, controller.initialized, controller.loading, controller.onCreate, provider]);

  const confirmation = useMemo<TenantOidcConfirmationViewModel | null>(() => {
    if (!confirmationKind || !provider) return null;
    const deleting = confirmationKind === 'delete';
    return {
      kind: confirmationKind,
      title: deleting ? 'Delete identity provider?' : 'Disable tenant sign-in?',
      description: deleting
        ? 'The provider must already be disabled. Its broker and stored secret will be removed.'
        : 'New tenant-provider logins will stop. Platform login remains available.',
      confirmLabel: deleting ? 'Delete provider' : 'Disable provider',
      destructive: true,
      onConfirm: () => {
        setConfirmationKind(null);
        if (deleting) provider.onDelete();
        else provider.onDisable();
      },
      onCancel: () => setConfirmationKind(null),
    };
  }, [confirmationKind, provider]);

  const pendingAction: TenantOidcPendingAction = controller.editor.submitting
    ? provider && controller.editor.draft.replaceSecret ? 'replace_secret' : 'save'
    : (provider?.busyAction as TenantOidcPendingAction | undefined) ?? null;
  const loginUrl = tenantLoginUrl(provider?.loginHostnameLabel ?? '');

  return (
    <TenantOidcSettingsView
      title="Tenant sign-in"
      description={`Configure the OIDC provider used from ${controller.tenantLabel}'s tenant-specific login address.`}
      loginUrlLabel="Tenant login address"
      loginUrl={loginUrl}
      canView={!controller.accessDenied}
      canManage={controller.canManage}
      loading={controller.loading}
      provider={provider ? {
        id: provider.id,
        displayName: provider.displayName,
        issuer: provider.issuer,
        clientId: controller.editor.draft.clientId,
        loginHostname: loginUrl ?? provider.loginHostnameLabel,
        desiredState: provider.desiredState,
        observedState: provider.observedState,
        desiredStateLabel: titleCase(provider.desiredState),
        observedStateLabel: titleCase(provider.observedState),
        statusTone: observedTone[provider.observedState],
        secretConfigured: provider.secretPresent,
        version: provider.version,
        lastValidatedAt: provider.validatedAt,
        lastSuccessfulLoginAt: provider.lastSuccessfulLoginAt,
        safeDiagnostic: provider.diagnosticCode ? titleCase(provider.diagnosticCode) : controller.error,
      } : null}
      clientSettings={controller.clientSettings}
      draft={{
        displayName: controller.editor.draft.displayName,
        issuer: controller.editor.draft.issuer,
        clientId: controller.editor.draft.clientId,
        clientSecret: controller.editor.draft.clientSecret,
        replaceSecret: controller.editor.draft.replaceSecret,
        roleClaimPath: controller.editor.draft.roleClaimPath,
        additionalScopes: controller.editor.draft.additionalScopes,
      }}
      validation={controller.editor.validation}
      pendingAction={pendingAction}
      confirmation={confirmation}
      roleMappings={provider ? controller.roleMappings : null}
      onDraftChange={(draft) => controller.editor.onDraftChange({ ...controller.editor.draft, ...draft })}
      onSaveDraft={controller.editor.onSubmit}
      onValidateProvider={() => provider?.onValidate()}
      onActivateProvider={() => provider?.onActivate()}
      onDisableProvider={() => setConfirmationKind('disable')}
      onDeleteProvider={() => setConfirmationKind('delete')}
      onCopyLoginUrl={() => { if (loginUrl) void navigator.clipboard.writeText(loginUrl); }}
      onCopyCallbackUrl={() => { if (controller.clientSettings) void navigator.clipboard.writeText(controller.clientSettings.callbackUrl); }}
      onRefresh={controller.onRefresh}
    />
  );
}
